import { useEffect } from "react";

const DEFAULT_DESC =
  "Trusted, culturally intelligent errands in Nigeria for the diaspora. Check-ins, deliveries, document collection and supervised hiring with verified, insured agents.";

/**
 * Lightweight, dependency-free SEO meta management.
 *
 *   useSEO({
 *     title: "Login — ErrandDay",
 *     description: "Sign in to ErrandDay…",
 *     noindex: true,            // adds <meta name="robots" content="noindex,follow">
 *     canonicalPath: "/login",  // overrides path; defaults to current pathname
 *   });
 *
 * Restores the previous tags on unmount.
 */
export default function useSEO({
  title,
  description,
  noindex = false,
  canonicalPath,
} = {}) {
  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const prevTitle = document.title;
    if (title) document.title = title;

    const prevDescription = upsertMeta(
      'meta[name="description"]',
      "name",
      "description",
      description || DEFAULT_DESC,
    );
    const prevRobots = noindex
      ? upsertMeta('meta[name="robots"]', "name", "robots", "noindex,follow")
      : null;

    // Canonical — always points to the apex domain
    const path = canonicalPath || (typeof window !== "undefined" ? window.location.pathname : "/");
    const canonicalUrl = `https://errandday.app${path}`;
    const prevCanonical = upsertLink("canonical", canonicalUrl);

    // Open Graph mirrors title + description for richer link previews
    const prevOgTitle = title ? upsertMeta('meta[property="og:title"]', "property", "og:title", title) : null;
    const prevOgDesc = upsertMeta('meta[property="og:description"]', "property", "og:description", description || DEFAULT_DESC);
    const prevTwitterTitle = title ? upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", title) : null;
    const prevTwitterDesc = upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", description || DEFAULT_DESC);

    return () => {
      if (title) document.title = prevTitle;
      restoreMeta('meta[name="description"]', "name", "description", prevDescription, DEFAULT_DESC);
      restoreMeta('meta[name="robots"]', "name", "robots", prevRobots, null);
      restoreLink("canonical", prevCanonical);
      if (title) restoreMeta('meta[property="og:title"]', "property", "og:title", prevOgTitle, null);
      restoreMeta('meta[property="og:description"]', "property", "og:description", prevOgDesc, DEFAULT_DESC);
      if (title) restoreMeta('meta[name="twitter:title"]', "name", "twitter:title", prevTwitterTitle, null);
      restoreMeta('meta[name="twitter:description"]', "name", "twitter:description", prevTwitterDesc, DEFAULT_DESC);
    };
  }, [title, description, noindex, canonicalPath]);
}

function upsertMeta(selector, attrName, attrValue, content) {
  let el = document.head.querySelector(selector);
  const prev = el ? el.getAttribute("content") : null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attrName, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return prev;
}

function restoreMeta(selector, attrName, attrValue, prev, fallback) {
  const el = document.head.querySelector(selector);
  if (!el) return;
  if (prev === null && fallback === null) {
    el.remove();
    return;
  }
  el.setAttribute("content", prev ?? fallback ?? "");
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  const prev = el ? el.getAttribute("href") : null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return prev;
}

function restoreLink(rel, prev) {
  const el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) return;
  if (prev === null) {
    el.remove();
    return;
  }
  el.setAttribute("href", prev);
}
